/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewContainerRef,
  NgZone,
  ViewChildren,
  QueryList
}                                 from '@angular/core';

import {
  Router,
  ActivatedRoute,
  NavigationStart
}                                from '@angular/router';

import { Subscription   }        from 'rxjs';

import * as $                    from 'jquery';

import { DockNode       }        from 'dock-spawn-ts/lib/js/DockNode';
import { DockManager    }        from 'dock-spawn-ts/lib/js/DockManager';
import { PanelContainer }        from 'dock-spawn-ts/lib/js/PanelContainer';
import { DockConfig     }        from 'dock-spawn-ts/lib/js/DockConfig';

import { ServiceManager }        from "@/services/service.manager";
import { Logger }                from '@/util/logger';
import { User }                  from '@/models/user';

import { Window, WindowType }    from './window';
import { ProjectWindow }         from './project.window';
import { PropertiesWindow }      from './properties.window';

import {
  DocumentWindowManager,
  DocumentTab
}                                from './document.window.manager';

import { ActionType }            from '@/models/action';
import { ProjectCloseAction }    from '@/actions/project.close.action';
import { ProjectDeleteAction }   from '@/actions/project.delete.action';
import { Project }               from '@/models/project';
import { PropertiesHolder }      from '@/models/properties.holder';
import {ScenarioBrowserMetadata} from '@/screens/ide/scenario.browser.metadata';
import {ThreeStringViewer}       from '@/screens/ide/stringviewer/three.string.viewer';

import {IDEHotKeys} from "@/screens/ide/ide.hot.keys";
import {ActionService} from "@/services/action.service";


@Component({
  templateUrl: 'ide.component.html'
})

/**
 *
 * @class IDEComponent
 *
 * @brief the main Integrated Development Environment (IDE) page.  The "home" for this
 * web application.  Essentially a "dockable view" play area for the views that can be
 * used by users.
 *
 * This page can't be displayed unless there is a logged in user (because the guards we
 * have on routes).  So we can safely assume that hte authentication service already
 * has a user.  For logout though, we subscribe to the user changes...watching for it go
 * null...and then we logout.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class IDEComponent implements OnDestroy, OnInit {

  @ViewChildren(ThreeStringViewer) stringLines!: QueryList<ThreeStringViewer>;

  /** the main tabbed windows view in middle of the GUI */
  private documents: DocumentWindowManager = null;

  /** the the currently logged in user */
  private user: User = null;

  /** the observable for router events (navigation/page changes) */
  private routeSubscription: Subscription;

  /** the observable for window toggle requests */
  private windowToggleSub: Subscription;

  /** the observable for tab close events */
  private tabCloseSub: Subscription;

  /** the observable for tab open events */
  private tabOpenSub: Subscription;

  /** the observable for user focus changes */
  private   focusSub: Subscription = null;

  /** the observable for active project */
  private projectSub: Subscription;

  /** the observable for the logged in user */
  private userWatch: Subscription;

  private dockManager: DockManager = null;

  private _projectView: ProjectWindow = null;

  private _propertiesView: PropertiesWindow = null;

  private _hotKeys: IDEHotKeys = null;

  constructor(
    private router:      Router,
    private route:       ActivatedRoute,
    private services:    ServiceManager,
    private actions:     ActionService,
    private view:        ViewContainerRef,
    private zone:        NgZone
  ) {

    Logger.debug('[IDE] constructing...');

    /*
    $('app-root').on('keydown', (e: KeyDownEvent) => {

      if ((e.key === 'Enter') || (e.key === 'Tab') || (e.key === 'Escape') || (e.key === 'Backspace')) {

        e.preventDefault();
        e.stopPropagation();

        return false;
      }
    });

     */

    /*
     * tell the IDE service what the view container is for /ide, this allows creating dynamic
     * components within that view tree.
     *
     */

    Logger.debug('[IDE] configuring /ide view container...');
    services.ide.setRootViewContainerRef(this.view);
  }

  /** @brief set up the dockable views and view manager */
  ngOnInit(): void {

    /*
     * watch for page changes, so we know to do any cleanup before leaving the IDE page, like clearing
     * floating dialogs.
     *
     */

    this.routeSubscription = this.router.events.subscribe(event => {

      if (event instanceof NavigationStart) {

        this.pageLeave();
      }
    });

    /*
     * watch for the logged in user to go null, it means we've been logged out
     * somehow, session timeout, SSO validation fail, or manual logout.
     *
     */

    this.userWatch = this.services.auth.observableUser
      .subscribe(user => {

        if (user) {

          /* still an active user! */

          Logger.debug('[IDE] user switched to: ', user);

          this.user = user;

          return ;
        }

        Logger.debug('[IDE] logging out...');

        /* clean up...auto-save, remove floating dialogs etc. */




        /* let them login again ... */

        this.router.navigate(['/login']);
      });

    /* - - - - - - - - IDE setup - - - - - - - - */

    Logger.debug('[IDE]', 'starting up...');

    const divDockContainer: JQuery<HTMLElement>   = $('#dock_div');
    const divDockManager: JQuery<HTMLElement>     = $('#my_dock_manager');

    /*
     * when DockSpawn adds panels, it doesn't actually report them under their positions
     * in the IDE layout; it just floats them in the dialog root context (by default its <body>),
     * and then sizes/positions them to "slot" into where space has been left in the IDE layout.
     * But, when Angular does routing, this means that the DOM for the panels, is now outside
     * the Angular component, and won't be updated by Angular, so if you change pages, for example
     * from the main IDE to the Admin page...you will get weird things happen, because the panel
     * content won't have been hidden.
     *
     * Same kind of effects can happen in the other direction.  Its all because the DOM for the
     * panels is not inside the Angular component.
     *
     * To avoid those problems, we tell DockSpawn tha root context for dialogs and stuff is the
     * Angular component.  Basically force it to keep the panel DOM closer to where it is supposed
     * to be.  DockSpawn is doing that "floating", because it needs flexibility for dealing with
     * panels in the IDE layout.
     *
     *
     */

    let root: HTMLElement|null                    = $(divDockContainer).parent(`ng-component`).get(0) ?? null;

    if (!root) {

      /* this is problematic, we have to be able to know what our parent Angular component DOM is */

      Logger.error('[IDE] could not find <ng-component> for Dock Spawn embedding.');
    }

    const config: DockConfig                      = new DockConfig();
    config.moveOnlyWithinDockConatiner            = true;
    config.dialogRootElement                      = root;
    this.dockManager                              = new DockManager(divDockManager.get(0), config);

    // @ts-ignore
    window.dockManager                            = this.dockManager;

    this.dockManager.initialize();

    /* Let the dock manager element fill in the entire screen */

    window.onresize = (): void => {

      this.zone.runOutsideAngular(() => {

        this.dockManager.resize(
          divDockContainer.get(0).clientWidth,
          divDockContainer.get(0).clientHeight
        );

      });
    };
    window.onresize(null);

    /* create the project view */

    this._projectView    = new ProjectWindow('#solution_window', this.dockManager, this.services.ide, this.services.modals);
    this._propertiesView = new PropertiesWindow('#properties_window', this.dockManager);

    const documentNode: DockNode = this.dockManager.context.model.documentManagerNode;

    Logger.debug('[IDE] project panel: ', this._projectView.panel());
    Logger.debug('[IDE] properties panel: ', this._projectView.panel());

    this.dockManager.dockLeft(documentNode,  this._projectView.panel(),    0.20);
    this.dockManager.dockRight(documentNode, this._propertiesView.panel(), 0.20);

    /* watch for panel close events, so we can bring down (and up) our panels in a controlled way */

    this.dockManager.addLayoutListener({
      onClosePanel: (dockManager: DockManager, panel: PanelContainer): void => {

        this.closingPanel(panel);
      }
    });

    /* watch for changes in user focus throughout the GUI */

    this.focusSub = this.services.ide.activeFocus.subscribe(( properties: PropertiesHolder) => {

      this._propertiesView.focus(properties);
    });

    /* watch for active project changes */

    this.projectSub = this.services.projects.observeProject.subscribe(project => {

      /* for the existing (old) project do any final saving */

      this.saveTabs();

      Logger.debug('[IDE] project switched to: ', project);

      /* update the Close Project action */

      const closeAction:  ProjectCloseAction  = this.actions.action(ActionType.Project_Close) as ProjectCloseAction;
      const deleteAction: ProjectDeleteAction = this.actions.action(ActionType.Project_Delete) as ProjectDeleteAction;

      if (!project) {

        deleteAction.setProject();
        closeAction.setProject();

        /* clear the fancy tree */

        if (this._projectView) {

          this._projectView.setProject();
        }

        return ;

      } else {

        deleteAction.setProject(project.name, project.key);
        closeAction.setProject(project.name, project.key);
      }

      this.actions.save(closeAction);
      this.actions.save(deleteAction);

      /* setup FancyTree with the new project ... */

      if (this._projectView) {

        this._projectView.setProject(project);
      }

      /* recover tabbed windows for this project */

      this.loadTabs();

    });

    /* watch for Window toggle (open/close) requests */

    this.windowToggleSub = this.services.ide.onWindowToggle().subscribe(type => {

      for (const window of this.windows()) {

        if (window.type === type) {

          if (window.isOpen()) {

            window.close();

          } else {

            const panel: PanelContainer = window.open();

            if (panel !== null) {

              this.dockWindow(type, panel);
            }
          }
        }
      }

    });

    /* create tabbed document manager */

    Logger.debug('[IDE] loading tab manager...');

    this.documents = new DocumentWindowManager(config, this.dockManager, this.services);

    this.services.ide.setDocumentManager(this.documents);

    /* TODO: add the start page tab */


    /* watch for tab closings */

    this.tabCloseSub = this.documents.onTabClose().subscribe(
      (tab: DocumentTab) => {

        if (!tab) {

          return ;
        }

        Logger.debug(`[IDE] (${tab.meta.title}) was closed, saving tabs.`);

        this.saveTabs();
      }
    );

    /* watch for tab openings */

    this.tabOpenSub = this.documents.onTabOpen().subscribe(
      (tab: DocumentTab) => {

        if (!tab) {

          return ;
        }

        Logger.debug(`[IDE] (${tab.meta.title}) was opened, saving tabs.`);

        this.saveTabs();
      }
    );


    /*
     * SN-1613, off by default, they can use the Window menu to enable if they want.
     *
     */

    this._propertiesView.close();

    /* good to go! */

    Logger.debug('[IDE] initialization complete.');

    /* look for any manual actions in the URL */

    const queryParamMap = this.route.snapshot['queryParamMap'];

    const actions   = queryParamMap.keys.
      filter(key => ['scenariobrowse', 'stringview'].
      includes(key.toLowerCase())).
      map(key => [key, queryParamMap.get(key)]);

    console.log('[IDE] init action: ', actions);

    for (const action of actions) {

      if (action[0] === 'scenariobrowse') {

        Logger.debug('[IDE] (init) scenario auto-browse: ', action[1]);

        this.services.ide.launchWindow(
          new ScenarioBrowserMetadata(
            '',
            '',
            action[1]
          )
        );


      } else if (action[0] === 'stringview') {

        Logger.debug('[IDE] (init) sting auto-view: ', action[1]);


      }
    }

    /* delegate handling of hot keys to our hot key controller */

    this._hotKeys = new IDEHotKeys(this._projectView, this.documents, this.services);

  }

  /** ensure the given window is actually open */
  ensureOpen(type: WindowType) {

    for (const window of this.windows()) {

      if (!window.isOpen()) {

        const panel: PanelContainer = window.open();

        if (panel !== null) {

          this.dockWindow(type, panel);
        }
      }
    }
  }

  /** re-dock a window that we are re-opening */
  dockWindow(type: WindowType, panel: PanelContainer) {

    const documentNode = this.dockManager.context.model.documentManagerNode;

    switch (type) {

      case WindowType.Project: {

        this.dockManager.dockLeft(documentNode, panel, 0.20);
        break;
      }

      case WindowType.Properties: {

        this.dockManager.dockRight(documentNode, panel, 0.20);
        break;
      }
    }

  }

  /** fetch all the IDE windows, regardless of open/close state */
  windows() : Window[]  {

    const views: Window[] = [
      this._projectView,
      this._propertiesView
    ];

    const docs = this.documents.windows();

    return views.concat(docs);
  }

  /** when the dock-able window manager closes a view, we need to tell that view to properly shut down */
  closingPanel(panel: PanelContainer) {

    /* which window is being closed? */

    for (const window of this.windows()) {

      if (window.panel(false) === panel) {

        window.close(true);

      }
    }
  }

  pageLeave() {

    Logger.debug('[IDE]', 'leaving page...');

    /*
    if (this.infovisDialog) {

      this.infovisDialog.destroy();
    }
    */

    /* save active tabs for project */

    this.saveTabs();
  }

  ngOnDestroy() {

    /* stop watching the logged in user */

    this.userWatch.unsubscribe();
    this.windowToggleSub.unsubscribe();
    this.projectSub.unsubscribe();
    this.tabCloseSub.unsubscribe();
    this.tabOpenSub.unsubscribe();
    this.focusSub.unsubscribe();
  }

  /** trigger a logout */
  logout() {

    this.services.auth.logout();

    /* save active tabs for project */

    this.saveTabs();
  }

  /** load tabs for the current project */
  loadTabs(project?: Project) {

    /* if not provided, fall back to existing/old project...if there is one */

    if (!project) {

      if (this._projectView) {

        project = this._projectView.project();
      }
    }

    if (!project) {

      /* nothing we can do */

      return;
    }

    const key:         string = `/users/${this.user.username}/projects/project-${project.name}/document-tabs.json`;

    /* TODO: load tabs */


  }

  /** save the current window tabs for project */
  saveTabs(project?: Project) {

    /* if not provided, fall back to existing/old project...if there is one */

    if (!project) {

      if (this._projectView) {

        project = this._projectView.project();
      }
    }

    if (!project) {

      /* nothing we can do */

      return;
    }

    const key:         string = `/users/${this.user.username}/projects/project-${project.name}/document-tabs.json`;

    /* TODO: save tabs! */




  }

}
